import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Spinner, Alert, Accordion, Badge, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import usePlan from '../hooks/usePlan';
import useIsMobile from '../hooks/useIsMobile';
import AuthContext from '../context/AuthContext';
import MetricCard from '../components/MetricCard';
import SectionPager from '../components/SectionPager';
import TopListWidget from '../components/TopListWidget';
import UpgradePromoCard from '../components/UpgradePromoCard';
import AlertPanel from '../components/AlertPanel';
import SalesTrendChart from '../components/SalesTrendChart';
import PeakTimeWidget from '../components/PeakTimeWidget';
import RetentionCard from '../components/RetentionCard';
import ProjectionWidget from '../components/ProjectionWidget';
import ProfitAnalysisCard from '../components/ProfitAnalysisCard';
import DateRangePicker from '../components/DateRangePicker';

import useCashRegisterStatus from '../hooks/useCashRegisterStatus';
import OpenCashRegisterModal from '../components/OpenCashRegisterModal';
import CloseCashRegisterModal from '../components/CloseCashRegisterModal';
import CatalogShareModal from '../components/CatalogShareModal';
import { FaCashRegister } from 'react-icons/fa';
import { Share2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';

const DashboardPage = () => {
    const { user, business, businessSettings } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { canViewStandardStats, canViewAdvancedStats } = usePlan();
    const isMobile = useIsMobile();

    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // Check for payment success
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const preapprovalId = queryParams.get('preapproval_id');
        const status = queryParams.get('status');

        if (preapprovalId) {
            toast.success('¡Suscripción creada exitosamente!', {
                duration: 5000,
                icon: '🎉',
            });
            // Clear query params to avoid showing toast on refresh
            navigate('/dashboard', { replace: true });
        } else if (status === 'approved') { // Fallback for payments
            toast.success('¡Pago aprobado!', {
                duration: 5000,
                icon: '🎉',
            });
            navigate('/dashboard', { replace: true });
        }
    }, [location, navigate]);

    // Loading and error states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Metrics states
    const [outstandingPayments, setOutstandingPayments] = useState(null);
    const [overduePayments, setOverduePayments] = useState(null);
    const [growthMetrics, setGrowthMetrics] = useState(null);
    const [averageTicket, setAverageTicket] = useState(null);
    const [topCustomers, setTopCustomers] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [dailySales, setDailySales] = useState(null);
    const [weeklySales, setWeeklySales] = useState(null);
    const [monthlySales, setMonthlySales] = useState(null);

    // Phase 2 Metrics
    const [salesTrends, setSalesTrends] = useState([]);
    const [peakTimes, setPeakTimes] = useState({ best_day: null, best_hour: null });
    const [retention, setRetention] = useState({ retention_rate: 0, returning_customers: 0, total_active_customers: 0 });
    const [projection, setProjection] = useState({ projected_revenue: 0, upcoming_payments_count: 0 });
    const [lowRotation, setLowRotation] = useState([]);

    // Phase 3 Metrics
    const [profitAnalysis, setProfitAnalysis] = useState(null);

    // UI States
    const [activeAlertKey, setActiveAlertKey] = useState(null); // Collapsed by default

    // Date Range State
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

    const handleStartDateChange = (date) => setStartDate(date);
    const handleEndDateChange = (date) => setEndDate(date);

    // Cash Register Logic
    const { isOpen: isCashRegisterOpen, loading: cashLoading, refetch: refetchCashStatus, cashRegister, canOpenAStation } = useCashRegisterStatus();
    const [showOpenCashModal, setShowOpenCashModal] = useState(false);
    const [showCloseCashModal, setShowCloseCashModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!hasPermission(PERMISSIONS.VIEW_DASHBOARD)) {
                setLoading(false);
                setError(t('dashboardPage.errors.permission'));
                return;
            }

            try {
                setLoading(true);

                // Fetch all metrics in parallel
                const [
                    outstandingRes,
                    overdueRes,
                    growthRes,
                    ticketRes,
                    customersRes,
                    productsRes,
                    dailyRes,
                    weeklyRes,
                    monthlyRes,
                    trendsRes,
                    peakRes,
                    retentionRes,
                    projectionRes,
                    lowRotationRes,
                    profitRes
                ] = await Promise.all([
                    api.get('/dashboard/outstanding-payments'),
                    api.get('/dashboard/overdue-payments'),
                    api.get('/dashboard/growth-metrics', { params: { startDate, endDate } }),
                    api.get('/dashboard/average-ticket', { params: { startDate, endDate } }),
                    api.get('/dashboard/top-customers', { params: { startDate, endDate } }),
                    api.get('/dashboard/top-products', { params: { startDate, endDate } }),
                    api.get('/sales/daily-sales', { params: { startDate, endDate } }),
                    api.get('/sales/weekly-sales', { params: { startDate, endDate } }),
                    api.get('/sales/monthly-sales', { params: { startDate, endDate } }),
                    api.get('/dashboard/sales-trends', { params: { startDate, endDate } }),
                    api.get('/dashboard/peak-times', { params: { startDate, endDate } }),
                    api.get('/dashboard/customer-retention', { params: { startDate, endDate } }),
                    api.get('/dashboard/revenue-projection'),
                    api.get('/dashboard/low-rotation-products'),
                    api.get('/dashboard/profit-analysis', { params: { startDate, endDate } })
                ]);

                setOutstandingPayments(outstandingRes.data);
                setOverduePayments(overdueRes.data);
                setGrowthMetrics(growthRes.data);
                setAverageTicket(ticketRes.data);
                setTopCustomers(customersRes.data.top_customers || []);
                setTopProducts(productsRes.data.top_products || []);
                setDailySales(dailyRes.data);
                setWeeklySales(weeklyRes.data);
                setMonthlySales(monthlyRes.data);

                // Phase 2 Data
                setSalesTrends(trendsRes.data || []);
                setPeakTimes(peakRes.data || { best_day: null, best_hour: null });
                setRetention(retentionRes.data || { retention_rate: 0, returning_customers: 0, total_active_customers: 0 });
                setProjection(projectionRes.data || { projected_revenue: 0, upcoming_payments_count: 0 });
                setProfitAnalysis(profitRes.data);
                setLowRotation(lowRotationRes.data.low_rotation_products || []);

            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError(t('dashboardPage.errors.fetchData'));
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [hasPermission, t, startDate, endDate]);

    // Generate alerts based on data
    const generateAlerts = () => {
        const alerts = [];

        // Urgent: Overdue payments
        if (overduePayments && overduePayments.overdue_sales_count > 0) {
            alerts.push({
                priority: 'urgent',
                title: t('dashboard.overduePaymentsAlert', {
                    count: overduePayments.overdue_sales_count
                }),
                description: t('dashboard.overduePaymentsDescription', {
                    amount: '$' + overduePayments.total_overdue_amount.toLocaleString('es-CO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                        style: 'decimal'
                    })
                }),
                action: {
                    label: t('dashboard.viewSales', 'Ver Ventas'),
                    onClick: () => navigate('/sales?status=OVERDUE')
                }
            });
        }

        // Warning: Low collection rate
        if (outstandingPayments && outstandingPayments.collection_rate < 70) {
            alerts.push({
                priority: 'warning',
                title: t('dashboard.lowCollectionRateAlert'),
                description: t('dashboard.lowCollectionRateDescription', {
                    rate: outstandingPayments.collection_rate.toFixed(2)
                })
            });
        }

        // Info: Growth trend
        if (growthMetrics && growthMetrics.monthly_growth_percentage > 10) {
            alerts.push({
                priority: 'info',
                title: t('dashboard.growthTrendAlert'),
                description: t('dashboard.growthTrendDescription', {
                    percentage: growthMetrics.monthly_growth_percentage.toFixed(2)
                })
            });
        }

        // Warning: Low rotation products
        if (lowRotation && lowRotation.length > 0) {
            alerts.push({
                priority: 'warning',
                title: t('dashboard.lowRotationAlert', { count: lowRotation.length }),
                description: t('dashboard.lowRotationDescription'),
                action: {
                    label: t('dashboard.viewProducts', 'Ver Productos'),
                    onClick: () => navigate('/products?sort=low_rotation')
                }
            });
        }

        return alerts;
    };

    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" />
                <p className="mt-3">{t('dashboard.loading')}</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (!hasPermission(PERMISSIONS.VIEW_DASHBOARD)) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{t('dashboardPage.errors.permission')}</Alert>
            </Container>
        );
    }

    const alerts = generateAlerts();

    // Las secciones se arman como datos para poder renderizarlas apiladas en escritorio o
    // dentro del pager horizontal en móvil. Una sección vale null cuando no aplica al plan:
    // así el pager no muestra una pestaña vacía.
    const financialHealthContent = (
        <Row className="row-eq-height">
            <Col md={3} className="mb-3">
                <MetricCard
                    title={t('dashboard.monthlyRevenue')}
                    value={monthlySales?.total_sales || 0}
                    previousValue={canViewStandardStats ? growthMetrics?.previous_month?.total_sales : undefined}
                    format="currency"
                    variant="success"
                    icon="💵"
                    subtitle={t('dashboard.salesCount', { count: monthlySales?.sales_count || 0 })}
                />
            </Col>
            <Col md={3} className="mb-3">
                <MetricCard
                    title={t('dashboard.profitAnalysis.totalExpenses')}
                    value={profitAnalysis?.total_expenses || 0}
                    format="currency"
                    variant="danger"
                    icon="💸"
                />
            </Col>
            <Col md={3} className="mb-3">
                <MetricCard
                    title={t('dashboard.outstandingPayments')}
                    value={outstandingPayments?.total_outstanding || 0}
                    format="currency"
                    variant="warning"
                    icon="⏳"
                    subtitle={t('dashboard.collectionRate') + ': ' + (outstandingPayments?.collection_rate || 100).toFixed(2) + '%'}
                />
            </Col>
            <Col md={3} className="mb-3">
                <MetricCard
                    title={t('dashboard.overduePayments')}
                    value={overduePayments?.total_overdue_amount || 0}
                    format="currency"
                    variant={overduePayments?.total_overdue_amount > 0 ? 'danger' : 'info'}
                    icon={overduePayments?.total_overdue_amount > 0 ? '🔴' : '✅'}
                    subtitle={t('dashboard.clientsWithOverdue', { count: overduePayments?.clients_with_overdue || 0 })}
                />
            </Col>
            <Col md={3} className="mb-3">
                <MetricCard
                    title={t('dashboard.averageTicket')}
                    value={averageTicket?.current_average_ticket || 0}
                    previousValue={canViewStandardStats ? averageTicket?.previous_average_ticket : undefined}
                    format="currency"
                    variant="primary"
                    icon="🎯"
                />
            </Col>
        </Row>
    );

    // Análisis de Ganancias (fase 3): la tarjeta real con el plan avanzado, el promo con el
    // estándar, y nada con el básico.
    let profitAnalysisContent = null;
    if (canViewAdvancedStats) {
        profitAnalysisContent = (
            <Row>
                <Col md={12} className="mb-3">
                    <ProfitAnalysisCard data={profitAnalysis} />
                </Col>
            </Row>
        );
    } else if (canViewStandardStats) {
        profitAnalysisContent = (
            <Row>
                <Col md={12} className="mb-3">
                    <UpgradePromoCard
                        title={t('dashboard.profitAnalysis.title')}
                        features={[
                            t('dashboard.profitAnalysis.feature1'),
                            t('dashboard.profitAnalysis.feature2'),
                            t('dashboard.profitAnalysis.feature3')
                        ]}
                        planName={t('planManagement.planNames.business_network')}
                    />
                </Col>
            </Row>
        );
    }

    // Análisis y Tendencias (fase 2)
    const analysisAndTrendsContent = canViewStandardStats ? (
        <>
            <Row>
                <Col md={8} className="mb-3">
                    <SalesTrendChart data={salesTrends} />
                </Col>
                <Col md={4}>
                    <Row className="row-eq-height">
                        <Col md={12} className="mb-3">
                            {canViewAdvancedStats ? (
                                <ProjectionWidget
                                    amount={projection.projected_revenue}
                                    count={projection.upcoming_payments_count}
                                />
                            ) : (
                                <UpgradePromoCard
                                    title={t('dashboard.projections')}
                                    features={[t('dashboard.revenueProjection')]}
                                    planName={t('planManagement.planNames.business_network')}
                                />
                            )}
                        </Col>
                        <Col md={12} className="mb-3">
                            {canViewAdvancedStats ? (
                                <RetentionCard
                                    rate={retention.retention_rate}
                                    returningCustomers={retention.returning_customers}
                                    totalCustomers={retention.total_active_customers}
                                />
                            ) : (
                                <UpgradePromoCard
                                    title={t('dashboard.retention')}
                                    features={[t('dashboard.customerRetention')]}
                                    planName={t('planManagement.planNames.business_network')}
                                />
                            )}
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="row-eq-height">
                <Col md={4} className="mb-3">
                    {canViewAdvancedStats ? (
                        <PeakTimeWidget
                            bestDay={peakTimes.best_day}
                            bestHour={peakTimes.best_hour}
                        />
                    ) : (
                        <UpgradePromoCard
                            title={t('dashboard.peakTimes')}
                            features={[t('dashboard.bestSellingTimes')]}
                            planName={t('planManagement.planNames.business_network')}
                        />
                    )}
                </Col>
                <Col md={4} className="mb-3">
                    <MetricCard
                        title={t('dashboard.weeklyGrowth')}
                        value={weeklySales?.total_sales || 0}
                        previousValue={growthMetrics?.previous_week?.total_sales}
                        format="currency"
                        icon="📊"
                        subtitle={growthMetrics?.weekly_growth_percentage !== undefined
                            ? t('dashboard.growthPercentage', { percentage: growthMetrics.weekly_growth_percentage.toFixed(2) })
                            : ''
                        }
                    />
                </Col>
                <Col md={4} className="mb-3">
                    <MetricCard
                        title={t('dashboard.monthlyGrowth')}
                        value={monthlySales?.total_sales || 0}
                        previousValue={growthMetrics?.previous_month?.total_sales}
                        format="currency"
                        icon="📈"
                        subtitle={growthMetrics?.monthly_growth_percentage !== undefined
                            ? t('dashboard.growthPercentage', { percentage: growthMetrics.monthly_growth_percentage.toFixed(2) })
                            : ''
                        }
                    />
                </Col>
            </Row>
        </>
    ) : (
        <Row>
            <Col md={12}>
                <UpgradePromoCard
                    title={t('dashboard.analysisAndTrends')}
                    features={[
                        t('dashboard.salesTrends'),
                        t('dashboard.growthComparisons'),
                        t('dashboard.topPerformers')
                    ]}
                    planName={t('planManagement.planNames.growth')}
                />
            </Col>
        </Row>
    );

    const topPerformersContent = canViewStandardStats ? (
        <Row className="row-eq-height">
            <Col md={6} className="mb-3">
                <TopListWidget
                    title={t('dashboard.topCustomers')}
                    items={topCustomers}
                    type="customers"
                    maxItems={5}
                />
            </Col>
            <Col md={6} className="mb-3">
                <TopListWidget
                    title={t('dashboard.topProducts')}
                    items={topProducts}
                    type="products"
                    maxItems={5}
                />
            </Col>
        </Row>
    ) : null;

    const sections = [
        { key: 'financialHealth', icon: '💰', label: t('dashboard.financialHealth'), content: financialHealthContent },
        { key: 'profitAnalysis', icon: '📈', label: t('dashboard.profitAnalysis.title'), content: profitAnalysisContent },
        { key: 'analysisAndTrends', icon: '📊', label: t('dashboard.analysisAndTrends'), content: analysisAndTrendsContent },
        { key: 'topPerformers', icon: '🏆', label: t('dashboard.topPerformers'), content: topPerformersContent },
    ].filter((section) => section.content);

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex align-items-center gap-2 mb-3">
                <h2 className="mb-0">{t('dashboardPage.title')}</h2>
                {business?.slug && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowShareModal(true)}
                        title={t('dashboardPage.shareCatalogButtonTitle')}
                        style={{ borderRadius: 20, padding: '2px 10px', fontSize: '0.8rem' }}
                    >
                        <Share2 size={13} className="me-1" />
                        {t('dashboardPage.shareCatalogButton')}
                    </Button>
                )}
            </div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">{t('dashboardPage.subtitle')}</p>
                {/* Cash Register Button */}
                {hasPermission(PERMISSIONS.VIEW_CASH_REGISTER) && !cashLoading && (
                    isCashRegisterOpen ? (
                        hasPermission(PERMISSIONS.CLOSE_CASH_REGISTER) && (
                            <Alert variant="info" className="mb-0 py-1 px-3 d-flex align-items-center">
                                <span className="me-2 d-none d-sm-inline">{t('cashRegisterPage.salesPageIntegration.cashRegisterOpen')}</span>
                                <Button
                                    variant="dark"
                                    size="sm"
                                    onClick={() => setShowCloseCashModal(true)}
                                >
                                    <FaCashRegister className="me-2" />
                                    {t('cashRegisterPage.salesPageIntegration.closeCashRegister')}
                                </Button>
                            </Alert>
                        )
                    ) : (
                        hasPermission(PERMISSIONS.OPEN_CASH_REGISTER) && canOpenAStation && (
                            <Alert variant="warning" className="mb-0 py-1 px-3 d-flex align-items-center">
                                <span className="me-2 d-none d-sm-inline">{t('cashRegisterPage.salesPageIntegration.cashRegisterClosed')}</span>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => setShowOpenCashModal(true)}
                                >
                                    <FaCashRegister className="me-2" />
                                    {t('cashRegisterPage.salesPageIntegration.openCashRegister')}
                                </Button>
                            </Alert>
                        )
                    )
                )}
            </div>

            <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
            />

            {/* Alerts Section (Collapsible) */}
            {alerts.length > 0 && (
                <Accordion
                    className="mt-4 mb-4"
                    activeKey={activeAlertKey}
                    onSelect={(k) => setActiveAlertKey(k)}
                >
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className="d-flex align-items-center">
                                <span className="me-2">⚠️ {t('dashboard.alertsAndActions')}</span>
                                <Badge bg="danger" pill>
                                    {alerts.length}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <AlertPanel alerts={alerts} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {isMobile ? (
                <SectionPager
                    sections={sections}
                    ariaLabel={t('dashboard.sectionNav.label')}
                />
            ) : (
                sections.map((section) => (
                    <React.Fragment key={section.key}>
                        <h4 className="mt-4 mb-3">{section.icon} {section.label}</h4>
                        {section.content}
                    </React.Fragment>
                ))
            )}

            {/* Cash Register Modals */}
            <OpenCashRegisterModal
                show={showOpenCashModal}
                onHide={() => setShowOpenCashModal(false)}
                onSuccess={() => {
                    refetchCashStatus();
                    toast.success(t('cashRegisterPage.salesPageIntegration.cashRegisterOpened'));
                    setShowOpenCashModal(false);
                }}
            />

            <CloseCashRegisterModal
                show={showCloseCashModal}
                onHide={() => setShowCloseCashModal(false)}
                cashRegister={cashRegister}
                onSuccess={() => {
                    refetchCashStatus();
                    // Toast is handled inside the modal
                }}
            />

            <CatalogShareModal
                show={showShareModal}
                onHide={() => setShowShareModal(false)}
                business={business}
            />
        </Container>
    );
};

export default DashboardPage;
